import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CaretDown, Flag } from "@phosphor-icons/react";
import { regionHealth, healthBand, toneColor } from "@/lib/laymen";

/**
 * Compact region table grouped by country → bundesland.
 * Replaces the long vertical stack of RegionPlainCard when > 8 regions.
 */
export default function RegionGroupedList({ regions, snapshots, countries, testid = "grouped-regions" }) {
  const snapMap = useMemo(() => {
    const m = {};
    (snapshots || []).forEach((s) => { m[s.region_id] = s; });
    return m;
  }, [snapshots]);

  // Group regions: country -> bundesland -> list
  const grouped = useMemo(() => {
    const g = {};
    regions.forEach((r) => {
      const c = r.country || "?";
      const b = r.bundesland || "?";
      g[c] = g[c] || {};
      g[c][b] = g[c][b] || [];
      g[c][b].push(r);
    });
    return g;
  }, [regions]);

  const [openCountry, setOpenCountry] = useState({ AT: true, DE: false });
  const [openBL, setOpenBL] = useState({});

  const toggleCountry = (c) => setOpenCountry((s) => ({ ...s, [c]: !s[c] }));
  const toggleBL = (key) => setOpenBL((s) => ({ ...s, [key]: !s[key] }));

  return (
    <section className="space-y-3" data-testid={testid}>
      {countries.map((co) => {
        const blMap = grouped[co.code] || {};
        const blEntries = Object.entries(blMap);
        const totalRegions = blEntries.reduce((a, [, list]) => a + list.length, 0);
        const avgScore =
          totalRegions === 0
            ? 0
            : Math.round(
                blEntries.reduce(
                  (acc, [, list]) =>
                    acc + list.reduce((a, r) => a + regionHealth(snapMap[r.id]?.metrics), 0),
                  0
                ) / totalRegions
              );
        const tone = toneColor(healthBand(avgScore).level);
        const isOpen = openCountry[co.code];
        return (
          <div key={co.code} className="eid-card overflow-hidden" data-testid={`country-${co.code}`}
               style={{ borderLeft: `3px solid ${co.tone}` }}>
            <button
              onClick={() => toggleCountry(co.code)}
              className="w-full px-5 py-4 flex items-center justify-between hover:bg-white/5 transition-colors"
              data-testid={`country-toggle-${co.code}`}
            >
              <div className="flex items-center gap-3">
                <span className="p-2 rounded-sm" style={{ background: `${co.tone}22`, color: co.tone }}>
                  <Flag size={18} />
                </span>
                <div className="text-left">
                  <div className="eid-label">{co.code}</div>
                  <div className="text-lg font-medium tracking-tight">{co.label}</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="eid-label">Ø Score</div>
                  <div className="font-mono text-xl" style={{ color: tone.fg }}>{avgScore}</div>
                </div>
                <div className="text-right hidden sm:block">
                  <div className="eid-label">Regionen</div>
                  <div className="font-mono text-xl text-white">{totalRegions}</div>
                </div>
                <CaretDown
                  size={16}
                  className="text-zinc-400 transition-transform"
                  style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0)" }}
                />
              </div>
            </button>
            {isOpen && (
              <div className="border-t border-white/10 bg-[#161616]" data-testid={`country-body-${co.code}`}>
                {blEntries.map(([bl, list]) => {
                  const blKey = `${co.code}.${bl}`;
                  const blAvg = Math.round(
                    list.reduce((a, r) => a + regionHealth(snapMap[r.id]?.metrics), 0) / list.length
                  );
                  const blTone = toneColor(healthBand(blAvg).level);
                  const blOpen = openBL[blKey] ?? list.length <= 3;
                  return (
                    <div key={blKey} className="border-b border-white/10 last:border-0"
                         data-testid={`bl-${blKey.replace(/[^a-z0-9]/gi, "-")}`}>
                      <button
                        onClick={() => toggleBL(blKey)}
                        className="w-full px-5 py-3 flex items-center justify-between hover:bg-white/5 transition-colors text-left"
                      >
                        <div className="flex items-center gap-3">
                          <span
                            className="inline-block w-2 h-2 rounded-full"
                            style={{ background: blTone.dot }}
                          />
                          <span className="text-sm font-medium">{bl}</span>
                          <span className="text-[11px] font-mono text-zinc-500">· {list.length} Region{list.length !== 1 ? "en" : ""}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-mono text-sm" style={{ color: blTone.fg }}>Ø {blAvg}</span>
                          <CaretDown
                            size={12}
                            className="text-zinc-500 transition-transform"
                            style={{ transform: blOpen ? "rotate(180deg)" : "rotate(0)" }}
                          />
                        </div>
                      </button>
                      {blOpen && (
                        <div className="px-5 pb-3">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                                <th className="text-left py-1.5">Region</th>
                                <th className="text-right py-1.5 hidden sm:table-cell">NDVI</th>
                                <th className="text-right py-1.5 hidden md:table-cell">Boden</th>
                                <th className="text-right py-1.5">Score</th>
                              </tr>
                            </thead>
                            <tbody>
                              {list.map((r) => {
                                const m = snapMap[r.id]?.metrics;
                                const s = regionHealth(m);
                                const t = toneColor(healthBand(s).level);
                                return (
                                  <tr key={r.id}
                                      className="border-t border-white/10 hover:bg-[#121212] transition-colors"
                                      data-testid={`region-row-${r.id}`}>
                                    <td className="py-1.5">
                                      <div className="font-medium text-[13px]">{r.label}</div>
                                      <div className="text-[11px] text-zinc-500">{r.description}</div>
                                    </td>
                                    <td className="py-1.5 text-right font-mono text-[12px] hidden sm:table-cell">
                                      {m?.ndvi_current?.toFixed?.(2) ?? "–"}
                                    </td>
                                    <td className="py-1.5 text-right font-mono text-[12px] hidden md:table-cell">
                                      {m?.soil_moisture_current?.toFixed?.(2) ?? "–"}
                                    </td>
                                    <td className="py-1.5 text-right">
                                      <span
                                        className="inline-block font-mono text-[12px] px-2 py-0.5 rounded-full"
                                        style={{ background: t.bg, color: t.fg }}
                                      >
                                        {s}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      <div className="text-xs text-zinc-500 font-mono text-right">
        <Link to="/map" className="underline hover:text-white" data-testid="to-map-link">
          → auf der Karte ansehen
        </Link>
      </div>
    </section>
  );
}
